/**
 * Module dependencies.
 */

import { ComponentPropsWithRef, forwardRef, useMemo } from 'react';
import isPropValid from '@emotion/is-prop-valid';
import styled from 'styled-components';
import { prop } from 'styled-tools';

/**
 * Export `SvgProps` interface.
 */

export type SvgProps = {
  color?: string;
  icon: TrustedHTML | string;
  size?: string;
} & ComponentPropsWithRef<'span'>;

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.span.withConfig({
  shouldForwardProp: propName => !['color', 'size'].includes(propName) && isPropValid(propName)
})<Omit<SvgProps, 'icon'>>`
  color: ${prop('color', 'currentColor')};
  display: inline-block;
  line-height: 0;
  position: relative;
  width: ${prop('size')};
`;

/**
 * Export `Svg` component.
 */

export const Svg = forwardRef<HTMLSpanElement, SvgProps>((props, ref) => {
  const { icon, ...rest } = props;
  const innerHtml = useMemo(
    () => ({
      __html: icon // eslint-disable-line id-match
    }),
    [icon]
  );

  return <Wrapper {...rest} dangerouslySetInnerHTML={innerHtml} ref={ref} />;
});

/**
 * Display name.
 */

Svg.displayName = 'Svg';
